// tslint:disable: variable-name
import { equals } from 'ramda';

import {
  IOfferHistoryRoomCountItemSchema,
  ERoomsCount,
} from '../../../repositories/valuation-offer-history/entities/offer_history/OfferHistoryRoomCountItemSchema';
import {
  IOfferHistoryStatusCountItemSchema,
  EStatus,
} from '../../../repositories/valuation-offer-history/entities/offer_history/OfferHistoryStatusCountItemSchema';

import { TRoomsFilters, TRoomsFilterItem, TStatusFilters, TStatusFilterItem, STATUS_ALL } from '../types';

/**
 * Надписи на кнопках фильтров, которые будет видеть юзер
 * */
const filtersLabels = {
  [ERoomsCount.Studio]: 'Студия',
  [ERoomsCount.One]: '1',
  [ERoomsCount.Two]: '2',
  [ERoomsCount.Three]: '3',
  [ERoomsCount.Four]: '4',
  [ERoomsCount.Five]: '5',
  [ERoomsCount.SixPlus]: '6+',
  [EStatus.Published]: 'В продаже',
  [EStatus.Deactivated]: 'Снято с продажи',
};

/**
 * маппер фильтров комнат
 * Если фильтров комнат меньше 2х - возвращает пустые фильтры (скрывает весь блок фильтров комнат)
 * Добавляет стандартный фильтр "Все"
 * */
type TPrepareRoomsFilters = (apiFilters: IOfferHistoryRoomCountItemSchema[]) => TRoomsFilters | null;

export const _prepareRoomsFilters: TPrepareRoomsFilters = apiFilters => {
  const filtersCount = apiFilters.length;

  if (filtersCount < 2) {
    return null;
  }

  const allFilter: TRoomsFilterItem = { id: STATUS_ALL, label: 'Все' };

  const preparedApiFilters = apiFilters.map(filter => {
    return {
      id: filter.roomsCount,
      label: filtersLabels[filter.roomsCount],
    };
  });

  return [allFilter, ...preparedApiFilters];
};

/**
 * маппер фильтров статусов
 * Если фильтров статусов меньше 2х - возвращает пустые фильтры (скрывает весь блок фильтров статусов)
 * Добавляет стандартный фильтр "Все"
 * */
type TPrepareStatusFilters = (apiFilters: IOfferHistoryStatusCountItemSchema[]) => TStatusFilters | null;

export const _prepareStatusFilters: TPrepareStatusFilters = apiFilters => {
  const filtersCount = apiFilters.length;

  /**
   * Если фильтр статусов меньше 2х - скрываем весь блок фильтров статусов
   * */
  if (filtersCount < 2) {
    return null;
  }

  const allFilter: TStatusFilterItem = { id: STATUS_ALL, label: 'Все' };

  const preparedApiFilters = apiFilters.map(filter => {
    return {
      id: filter.status,
      label: filtersLabels[filter.status],
    };
  });

  return [allFilter, ...preparedApiFilters];
};

/**
 * Надстройка над мапперами фильтров
 * Добавляет мемоизацию, кэширует входящие (apiFilters) и выходящие (preparedFilters) параметры
 * Это нужно для того, чтоб при повторных запросах к апи, фильтры не пересобирались в новый объект, структура которого
 * точно такая же, как и до этого ( проблема {a: 1} !== {a: 1} )
 *  * S - маппер фильтров
 *  * AF - apiFilters, то что приходит из api
 *  * PF - preparedFilters - фильтры, которые будем хранить в сторе
 * */
const prepareFilters = <AF, PF, S extends (apiFilters: AF) => PF>(filtersMapper: S): ((apiFilters: AF) => PF) => {
  let __apiFiltersCache: AF | null = null;
  let __preparedFiltersCache: PF | null = null;

  return apiFilters => {
    if (__preparedFiltersCache && __apiFiltersCache && equals(__apiFiltersCache, apiFilters)) {
      return __preparedFiltersCache;
    }

    const preparedFilters = filtersMapper(apiFilters);

    __apiFiltersCache = apiFilters;
    __preparedFiltersCache = preparedFilters;

    return preparedFilters;
  };
};

/**
 * Клиентские функции для подготовки фильтров комнат и статусов
 * */
export const prepareRoomsFilters = prepareFilters<
  IOfferHistoryRoomCountItemSchema[],
  TRoomsFilters | null,
  TPrepareRoomsFilters
>(_prepareRoomsFilters);

export const prepareStatusFilters = prepareFilters<
  IOfferHistoryStatusCountItemSchema[],
  TStatusFilters | null,
  TPrepareStatusFilters
>(_prepareStatusFilters);
